import { useEffect, useState } from "react";
import { api, API, formatApiErrorDetail } from "../lib/api";
import { CodeBlock } from "../components/CodeBlock";
import { Layout } from "../components/Layout";
import { toast } from "sonner";
import {
  Printer,
  Plus,
  Trash,
  Lightning,
  FloppyDisk,
  Archive,
  DownloadSimple,
  Trash as TrashIcon,
  Sparkle,
  Warning,
  CheckCircle,
  Package as PackageIcon,
} from "@phosphor-icons/react";

// --- Client-side lpadmin preview + validation (mirrors the backend builder) ---
const sanitizeQueue = (q) => ((q || "").trim().replace(/[^A-Za-z0-9._-]/g, "_")) || "printer";
const printerUri = (protocol, address) => {
  const a = (address || "").trim();
  if (a.includes("://")) return a;
  switch (protocol) {
    case "ipp": return `ipp://${a}/ipp/print`;
    case "ipps": return `ipps://${a}/ipp/print`;
    case "lpd": return `lpd://${a}`;
    case "socket": return a.includes(":") ? `socket://${a}` : `socket://${a}:9100`;
    case "smb": return `smb://${a}`;
    default: return `ipp://${a}/ipp/print`;
  }
};
const driverFlag = (mode, ppd) => {
  if (mode === "generic-ps") return "-m drv:///sample.drv/generic.ppd";
  if (mode === "generic-pcl") return "-m drv:///sample.drv/generpcl.ppd";
  if (mode === "ppd") return `-P "${ppd || "<PPD path>"}"`;
  return "-m everywhere";
};
const lpadminPreview = (p) => {
  const q = sanitizeQueue(p.queue);
  const uri = printerUri(p.protocol, p.address || "<address>");
  const drv = driverFlag(p.driver_mode, p.ppd_path);
  const sides = p.duplex ? "two-sided-long-edge" : "one-sided";
  const color = p.color ? "color" : "monochrome";
  const args = [`-p "${q}"`, "-E", `-v "${uri}"`, drv, `-D "${p.display_name}"`];
  if (p.location) args.push(`-L "${p.location}"`);
  args.push("-o printer-is-shared=false", `-o sides=${sides}`, `-o print-color-mode=${color}`);
  (p.options || "").split(/\s+/).filter(Boolean).forEach((t) => args.push(`-o ${t}`));
  return "/usr/sbin/lpadmin " + args.join(" ");
};
const validatePrinter = (p) => {
  const warns = [];
  if (!p.queue?.trim()) warns.push("Queue name is empty.");
  else if (/\s/.test(p.queue)) warns.push("Queue name has spaces — they'll be converted to underscores.");
  if (!p.address?.trim()) warns.push("Address (host / IP) is empty — the queue won't reach a device.");
  if (p.driver_mode === "ppd" && !p.ppd_path?.trim()) warns.push("Custom PPD selected but no PPD path was given.");
  if (p.driver_mode === "ppd" && !p.driver_pkg_url?.trim()) warns.push("PPD queues usually need a vendor .pkg — add a driver URL, or make sure the PPD is already on the Mac.");
  (p.options || "").split(/\s+/).filter(Boolean).forEach((t) => { if (!t.includes("=")) warns.push(`Option "${t}" is not in key=value form.`); });
  return warns;
};

const blankPrinter = () => ({
  queue: "Office_Printer",
  display_name: "Office Printer",
  location: "",
  protocol: "ipp",
  address: "",
  driver_mode: "driverless",
  ppd_path: "",
  driver_pkg_url: "",
  is_default: false,
  duplex: true,
  color: true,
  options: "",
});

export default function Printers() {
  const [providers, setProviders] = useState([]);
  const [meta, setMeta] = useState({ protocols: [], drivers: [] });
  const [projects, setProjects] = useState([]);
  const [action, setAction] = useState("install");
  const [provider, setProvider] = useState("jamf");
  const [printers, setPrinters] = useState([blankPrinter()]);
  const [result, setResult] = useState(null);
  const [building, setBuilding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bundling, setBundling] = useState(false);
  const [saveTarget, setSaveTarget] = useState("new");
  const [title, setTitle] = useState("Printer deployment");
  const [aiDesc, setAiDesc] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [offlineLoading, setOfflineLoading] = useState(false);

  useEffect(() => {
    api.get("/providers").then((r) => setProviders(r.data)).catch(() => {});
    api.get("/printers/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const update = (i, key, val) =>
    setPrinters((list) => list.map((p, idx) => (idx === i ? { ...p, [key]: val } : p)));
  const addPrinter = () => setPrinters((l) => [...l, blankPrinter()]);
  const removePrinter = (i) => setPrinters((l) => (l.length > 1 ? l.filter((_, idx) => idx !== i) : l));

  const build = async () => {
    setBuilding(true);
    setResult(null);
    try {
      const { data } = await api.post("/printers/build", { action, provider, printers });
      setResult(data);
      toast.success("Script generated — 0 tokens used");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Build failed");
    } finally {
      setBuilding(false);
    }
  };

  const aiSuggest = async () => {
    if (!aiDesc.trim()) { toast.error("Describe the printer you want to add first"); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/printers/ai-suggest", { description: aiDesc, provider });
      const suggested = { ...blankPrinter(), ...data.printer };
      setPrinters((list) => {
        const only = list.length === 1 ? list[0] : null;
        const isBlankDefault = only && only.queue === "Office_Printer" && !only.address;
        return isBlankDefault ? [suggested] : [...list, suggested];
      });
      setAction("install");
      toast.success(data.notes ? `Added: ${data.notes}` : "Printer added from your description — review the fields");
      setAiDesc("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI could not build that printer");
    } finally {
      setAiLoading(false);
    }
  };

  const downloadOffline = async () => {
    setOfflineLoading(true);
    try {
      const resp = await fetch(`${API}/printers/full-package`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ action: "install", provider, printers }),
      });
      if (!resp.ok) {
        let msg = "Offline package failed";
        try { const j = await resp.json(); msg = j.detail || msg; } catch { /* non-json */ }
        toast.error(msg);
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${provider}-printers-offline.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Offline package downloaded (drivers baked in)");
    } catch {
      toast.error("Offline package download failed");
    } finally {
      setOfflineLoading(false);
    }
  };

  const save = async () => {
    if (!result) return;
    setSaving(true);
    try {
      if (saveTarget === "new") {
        const { data } = await api.post("/projects", {
          title: title || "Printer deployment",
          provider: result.provider,
          output_type: result.output_type,
          prompt: `Printer ${action} (${printers.length})`,
          content: result.content,
          language: result.language,
          filename: result.filename,
          deploy_notes: result.deploy_notes,
        });
        setProjects((p) => [data, ...p]);
        setSaveTarget(data.id);
        toast.success("Saved as new project");
      } else {
        const { data } = await api.post(`/projects/${saveTarget}/versions`, {
          content: result.content,
          language: result.language,
          filename: result.filename,
          deploy_notes: result.deploy_notes,
          prompt: `Printer ${action}`,
          note: `Printer ${action}`,
        });
        setProjects((list) => list.map((p) => (p.id === data.id ? data : p)));
        toast.success(`Saved new version to "${data.title}"`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const downloadBundle = async () => {
    if (!result) return;
    setBundling(true);
    try {
      const resp = await fetch(`${API}/bundle`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          title: title || "Printer deployment",
          provider: result.provider,
          output_type: result.output_type,
          content: result.content,
          filename: result.filename,
          deploy_notes: result.deploy_notes,
        }),
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${result.provider}-printers.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Bundle downloaded");
    } catch {
      toast.error("Bundle download failed");
    } finally {
      setBundling(false);
    }
  };

  const inputCls =
    "w-full bg-[#050505] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200";
  const labelCls = "block font-mono text-[10px] text-[#71717A] mb-1.5 uppercase tracking-wider";

  return (
    <Layout>
      <div className="border-b border-[#27272A] px-8 py-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl tracking-tight flex items-center gap-2">
            <Printer size={22} className="text-[#00F0FF]" /> Printer Automation
          </h1>
          <p className="text-sm text-[#A1A1AA] font-mono mt-1">
            Build lpadmin install &amp; removal scripts for one or many printers.
          </p>
        </div>
        <span className="hidden md:flex items-center gap-2 font-mono text-xs text-[#00FF41] border border-[#00FF41]/40 bg-[#00FF41]/5 px-3 py-1.5">
          <Lightning size={13} weight="fill" /> 0 LLM TOKENS
        </span>
      </div>

      <div className="p-8 space-y-8 max-w-5xl">
        {/* AI-assisted setup */}
        <div className="border border-[#B37FEB]/40 bg-[#B37FEB]/5 p-5" data-testid="printer-ai-panel">
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-[#C79BF0] mb-3">
            <Sparkle size={14} weight="fill" /> Quick add with AI
          </div>
          <div className="flex flex-col md:flex-row gap-3">
            <input
              data-testid="printer-ai-input"
              value={aiDesc}
              onChange={(e) => setAiDesc(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && aiSuggest()}
              placeholder='Describe a printer, e.g. "Xerox on the 3rd floor at 10.0.1.50, A4 duplex, no color"'
              className="flex-1 bg-[#050505] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#B37FEB] focus:outline-none transition-colors duration-200"
            />
            <button
              data-testid="printer-ai-btn"
              onClick={aiSuggest}
              disabled={aiLoading}
              className="flex items-center justify-center gap-1.5 shrink-0 bg-[#B37FEB] text-[#0A0A0B] font-medium px-4 py-2 text-sm hover:bg-[#a066e0] transition-colors duration-200 disabled:opacity-50"
            >
              <Sparkle size={15} weight="fill" /> {aiLoading ? "Building…" : "Build printer with AI"}
            </button>
          </div>
          <p className="font-mono text-[10px] text-[#71717A] mt-2">
            AI fills the protocol, driver mode, address and options — then review the fields below before building.
          </p>
        </div>

        {/* Action + provider */}
        <div className="flex flex-wrap items-end gap-6">
          <div>
            <label className={labelCls}>Action</label>
            <div className="flex">
              {[
                { id: "install", label: "Install", icon: DownloadSimple },
                { id: "remove", label: "Remove", icon: TrashIcon },
              ].map((a) => (
                <button
                  key={a.id}
                  data-testid={`printer-action-${a.id}`}
                  onClick={() => {
                    setAction(a.id);
                    setResult(null);
                  }}
                  className={`flex items-center gap-2 px-4 py-2 border text-sm transition-colors duration-200 ${
                    action === a.id
                      ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/5"
                      : "border-[#27272A] text-[#A1A1AA] hover:border-[#3f3f46]"
                  }`}
                >
                  <a.icon size={15} /> {a.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={labelCls}>MDM Provider</label>
            <div className="flex flex-wrap gap-2">
              {providers.map((p) => (
                <button
                  key={p.id}
                  data-testid={`printer-provider-${p.id}`}
                  onClick={() => setProvider(p.id)}
                  className={`flex items-center gap-2 px-3 py-2 border text-xs transition-colors duration-200 ${
                    provider === p.id ? "border-[#00F0FF] text-[#00F0FF]" : "border-[#27272A] text-[#A1A1AA] hover:border-[#3f3f46]"
                  }`}
                >
                  <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
                  {p.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Printer rows */}
        <div className="space-y-4" data-testid="printer-rows">
          {printers.map((p, i) => (
            <div key={i} data-testid={`printer-row-${i}`} className="border border-[#27272A] bg-[#121214] p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="font-mono text-xs text-[#00F0FF]">PRINTER {i + 1}</span>
                {printers.length > 1 && (
                  <button
                    data-testid={`remove-printer-${i}`}
                    onClick={() => removePrinter(i)}
                    className="text-[#71717A] hover:text-[#FF5F56] transition-colors duration-200"
                  >
                    <Trash size={16} />
                  </button>
                )}
              </div>

              {action === "remove" ? (
                <div>
                  <label className={labelCls}>Queue name to remove</label>
                  <input
                    data-testid={`printer-queue-${i}`}
                    value={p.queue}
                    onChange={(e) => update(i, "queue", e.target.value)}
                    className={inputCls}
                    placeholder="Office_Printer"
                  />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Queue name (no spaces)</label>
                    <input data-testid={`printer-queue-${i}`} value={p.queue} onChange={(e) => update(i, "queue", e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Display name</label>
                    <input data-testid={`printer-display-${i}`} value={p.display_name} onChange={(e) => update(i, "display_name", e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Protocol</label>
                    <select data-testid={`printer-protocol-${i}`} value={p.protocol} onChange={(e) => update(i, "protocol", e.target.value)} className={inputCls}>
                      {meta.protocols.map((pr) => (
                        <option key={pr.id} value={pr.id}>{pr.name} — {pr.hint}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Address (host / IP {p.protocol === "smb" ? "server/share" : ""})</label>
                    <input data-testid={`printer-address-${i}`} value={p.address} onChange={(e) => update(i, "address", e.target.value)} className={inputCls} placeholder="10.0.1.50 or printer.corp.local" />
                  </div>
                  <div>
                    <label className={labelCls}>Driver</label>
                    <select data-testid={`printer-driver-${i}`} value={p.driver_mode} onChange={(e) => update(i, "driver_mode", e.target.value)} className={inputCls}>
                      {meta.drivers.map((dr) => (
                        <option key={dr.id} value={dr.id}>{dr.name}</option>
                      ))}
                    </select>
                  </div>
                  {p.driver_mode === "ppd" ? (
                    <div>
                      <label className={labelCls}>PPD path on Mac</label>
                      <input data-testid={`printer-ppd-${i}`} value={p.ppd_path} onChange={(e) => update(i, "ppd_path", e.target.value)} className={inputCls} placeholder="/Library/Printers/PPDs/..." />
                    </div>
                  ) : (
                    <div>
                      <label className={labelCls}>Location (optional)</label>
                      <input value={p.location} onChange={(e) => update(i, "location", e.target.value)} className={inputCls} placeholder="3rd Floor" />
                    </div>
                  )}
                  {p.driver_mode === "ppd" && (
                    <div>
                      <label className={labelCls}>Location (optional)</label>
                      <input value={p.location} onChange={(e) => update(i, "location", e.target.value)} className={inputCls} placeholder="3rd Floor" />
                    </div>
                  )}
                  <div>
                    <label className={labelCls}>Extra lpadmin options (space-sep key=value)</label>
                    <input value={p.options} onChange={(e) => update(i, "options", e.target.value)} className={inputCls} placeholder="media=A4 Collate=True" />
                  </div>

                  {p.driver_mode !== "driverless" && (
                    <div className="md:col-span-2">
                      <label className={labelCls}>Vendor driver .pkg URL (optional — installed before adding the queue)</label>
                      <input
                        data-testid={`printer-driverpkg-${i}`}
                        value={p.driver_pkg_url}
                        onChange={(e) => update(i, "driver_pkg_url", e.target.value)}
                        className={inputCls}
                        placeholder="https://…/VendorPrintDriver.pkg"
                      />
                      <p className="font-mono text-[10px] text-[#71717A] mt-1.5">
                        {p.driver_mode === "ppd"
                          ? "Required unless the PPD is already on the Mac. The script verifies the PPD exists and skips the queue if missing."
                          : "Generic PS/PCL PPDs may be absent on macOS 12+. Provide a vendor driver .pkg or use Driverless."}
                      </p>
                    </div>
                  )}

                  <div className="md:col-span-2 flex flex-wrap gap-6 pt-1">
                    {[
                      { key: "is_default", label: "Set as default" },
                      { key: "duplex", label: "Duplex (two-sided)" },
                      { key: "color", label: "Color" },
                    ].map((t) => (
                      <button
                        key={t.key}
                        data-testid={`printer-${t.key}-${i}`}
                        onClick={() => update(i, t.key, !p[t.key])}
                        className="flex items-center gap-2 text-sm text-[#A1A1AA]"
                      >
                        <span
                          className={`w-10 h-5 border flex items-center px-0.5 transition-colors duration-200 ${
                            p[t.key] ? "border-[#00F0FF] bg-[#00F0FF]/10 justify-end" : "border-[#27272A] justify-start"
                          }`}
                        >
                          <span className={`w-3.5 h-3.5 ${p[t.key] ? "bg-[#00F0FF]" : "bg-[#52525b]"}`} />
                        </span>
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {action === "install" && (
                <div className="mt-4 border-t border-[#27272A] pt-4" data-testid={`printer-preview-${i}`}>
                  <div className="font-mono text-[9px] uppercase tracking-wider text-[#71717A] mb-1.5">lpadmin command preview</div>
                  <pre className="bg-[#050505] border border-[#27272A] p-3 overflow-x-auto font-mono text-[11px] text-[#00FF41] leading-relaxed whitespace-pre-wrap">{lpadminPreview(p)}</pre>
                  {validatePrinter(p).length > 0 ? (
                    <ul className="mt-2 space-y-1" data-testid={`printer-warnings-${i}`}>
                      {validatePrinter(p).map((w, wi) => (
                        <li key={wi} className="flex items-start gap-2 text-[11px] text-[#FEBC2E] font-mono leading-relaxed">
                          <Warning size={13} weight="fill" className="mt-0.5 shrink-0" /> {w}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-2 flex items-center gap-1.5 text-[11px] text-[#00FF41] font-mono">
                      <CheckCircle size={13} weight="fill" /> Looks good — ready to build.
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}

          <button
            data-testid="add-printer-btn"
            onClick={addPrinter}
            className="flex items-center gap-2 border border-dashed border-[#27272A] text-[#A1A1AA] px-4 py-2.5 text-sm hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors duration-200 w-full justify-center"
          >
            <Plus size={16} /> Add another printer (bulk)
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            data-testid="build-printer-btn"
            onClick={build}
            disabled={building}
            className="flex items-center gap-2 bg-[#00F0FF] text-[#0A0A0B] font-medium px-6 py-3 text-sm hover:bg-[#00d4e0] transition-colors duration-200 disabled:opacity-50"
          >
            <Lightning size={16} weight="fill" /> {building ? "Building…" : `Build ${action} script (0 tokens)`}
          </button>
          {action === "install" && (
            <button
              data-testid="printer-offline-btn"
              onClick={downloadOffline}
              disabled={offlineLoading}
              title="Bundle vendor drivers into a self-contained ZIP that installs with no internet"
              className="flex items-center gap-2 border border-[#00FF41] text-[#00FF41] px-5 py-3 text-sm hover:bg-[#00FF41]/10 transition-colors duration-200 disabled:opacity-50"
            >
              <PackageIcon size={16} weight="fill" /> {offlineLoading ? "Bundling installers…" : "Download offline package"}
            </button>
          )}
        </div>

        {result && (
          <div className="space-y-4" data-testid="printer-output">
            <div className="flex flex-col md:flex-row gap-3">
              <select
                data-testid="printer-save-target"
                value={saveTarget}
                onChange={(e) => setSaveTarget(e.target.value)}
                className="flex-1 bg-[#050505] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200"
              >
                <option value="new">＋ New project</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>↳ Add version to: {p.title}</option>
                ))}
              </select>
              {saveTarget === "new" && (
                <input
                  data-testid="printer-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="flex-1 bg-[#050505] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200"
                />
              )}
              <div className="flex gap-3">
                <button data-testid="printer-save-btn" onClick={save} disabled={saving} className="flex items-center gap-2 border border-[#00FF41] text-[#00FF41] px-4 py-2 text-sm hover:bg-[#00FF41]/10 transition-colors duration-200 disabled:opacity-50">
                  <FloppyDisk size={16} /> {saving ? "…" : "Save"}
                </button>
                <button data-testid="printer-bundle-btn" onClick={downloadBundle} disabled={bundling} className="flex items-center gap-2 border border-[#0A84FF] text-[#0A84FF] px-4 py-2 text-sm hover:bg-[#0A84FF]/10 transition-colors duration-200 disabled:opacity-50">
                  <Archive size={16} /> {bundling ? "…" : "Bundle"}
                </button>
              </div>
            </div>

            <CodeBlock code={result.content} language={result.language} filename={result.filename} />

            {result.deploy_notes && (
              <div className="border border-[#27272A] bg-[#121214] p-4">
                <div className="font-mono text-xs text-[#00F0FF] mb-2">DEPLOYMENT · {result.provider.toUpperCase()}</div>
                <p className="text-sm text-[#A1A1AA] leading-relaxed">{result.deploy_notes}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
